import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * TextInput Component (Web Implementation)
 *
 * A form input component with theme integration, validation states,
 * and comprehensive accessibility features.
 */
import { forwardRef, useState } from 'react';
import { useTheme } from '../../theme';
export const TextInput = forwardRef(({ variant = 'outlined', size = 'md', type = 'text', label, error, helperText, hasError = false, required = false, fullWidth = false, iconBefore, iconAfter, disabled, className, id, 'aria-describedby': ariaDescribedBy, 'data-testid': testId, ...props }, ref) => {
    const theme = useTheme();
    const [isFocused, setIsFocused] = useState(false);
    // Generate IDs for accessibility
    const inputId = id || `text-input-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = error ? `${inputId}-error` : undefined;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const describedBy = [ariaDescribedBy, errorId, helperId].filter(Boolean).join(' ') || undefined;
    const isError = hasError || !!error;
    const getContainerStyles = () => {
        return {
            display: 'inline-flex',
            flexDirection: 'column',
            gap: theme.spacing.xs,
            width: fullWidth ? '100%' : 'auto',
        };
    };
    const getInputWrapperStyles = () => {
        const baseStyles = {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.sm,
            position: 'relative',
            transition: 'all 0.2s ease',
        };
        const sizeStyles = {
            sm: {
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                fontSize: theme.typography.fontSize.small,
            },
            md: {
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: theme.typography.fontSize.body,
            },
            lg: {
                padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                fontSize: theme.typography.fontSize.h6,
            },
        };
        const variantStyles = {
            outlined: {
                border: `1px solid ${isError ? theme.colors.error : isFocused ? theme.colors.primary : theme.colors.border}`,
                borderRadius: theme.borderRadius.md,
                backgroundColor: theme.colors.background,
            },
            filled: {
                border: `1px solid ${isError ? theme.colors.error : 'transparent'}`,
                borderRadius: theme.borderRadius.md,
                backgroundColor: theme.colors.surface,
            },
            underlined: {
                borderBottom: `2px solid ${isError ? theme.colors.error : isFocused ? theme.colors.primary : theme.colors.border}`,
                backgroundColor: 'transparent',
            },
        };
        return {
            ...baseStyles,
            ...sizeStyles[size],
            ...variantStyles[variant],
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? 'not-allowed' : 'text',
        };
    };
    const getInputStyles = () => {
        return {
            flex: 1,
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
            color: theme.colors.text,
            fontFamily: theme.typography.fontFamily,
            fontSize: 'inherit',
            padding: 0,
        };
    };
    const getLabelStyles = () => {
        return {
            fontSize: theme.typography.fontSize.small,
            fontWeight: theme.typography.fontWeight.medium,
            color: isError ? theme.colors.error : theme.colors.text,
            marginBottom: theme.spacing.xs,
        };
    };
    const getHelperTextStyles = () => {
        return {
            fontSize: theme.typography.fontSize.caption,
            color: isError ? theme.colors.error : theme.colors.textMuted,
            marginTop: theme.spacing.xs,
        };
    };
    return (_jsxs("div", { style: getContainerStyles(), className: className, "data-testid": testId, children: [label && (_jsxs("label", { htmlFor: inputId, style: getLabelStyles(), children: [label, required && (_jsx("span", { "aria-label": "required", style: { color: theme.colors.error, marginLeft: '4px' }, children: "*" }))] })), _jsxs("div", { style: getInputWrapperStyles(), onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), children: [iconBefore && (_jsx("span", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            color: isError ? theme.colors.error : theme.colors.textMuted,
                        }, children: iconBefore })), _jsx("input", { ref: ref, id: inputId, type: type, disabled: disabled, required: required, "aria-invalid": isError, "aria-describedby": describedBy, "aria-required": required, style: getInputStyles(), ...props }), iconAfter && (_jsx("span", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            color: isError ? theme.colors.error : theme.colors.textMuted,
                        }, children: iconAfter }))] }), error && (_jsx("div", { id: errorId, role: "alert", style: getHelperTextStyles(), children: error })), helperText && !error && (_jsx("div", { id: helperId, style: getHelperTextStyles(), children: helperText }))] }));
});
TextInput.displayName = 'TextInput';
